import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import * as React from 'react';
import { type DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';

import { cn } from '@/client/lib/utils';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const d = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn('group/calendar select-none p-3 [--cell-size:2.25rem]', className)}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString('en-GB', { month: 'long' }),
        formatWeekdayName: (date) => date.toLocaleString('en-GB', { weekday: 'short' }).slice(0, 2),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', d.root),
        months: cn('relative flex flex-col gap-4 sm:flex-row', d.months),
        month: cn('flex w-full flex-col gap-3', d.month),
        nav: cn('absolute inset-x-0 top-0 flex h-8 w-full items-center justify-between', d.nav),
        button_previous: cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors',
          'hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:pointer-events-none disabled:opacity-30',
          d.button_previous,
        ),
        button_next: cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors',
          'hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:pointer-events-none disabled:opacity-30',
          d.button_next,
        ),
        month_caption: cn('flex h-8 w-full items-center justify-center px-8', d.month_caption),
        dropdowns: cn(
          'flex h-8 items-center justify-center gap-0.5 text-sm font-semibold',
          d.dropdowns,
        ),
        dropdown_root: cn(
          'relative inline-flex items-center rounded-md transition-colors hover:bg-accent',
          'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
          d.dropdown_root,
        ),
        dropdown: cn('absolute inset-0 w-full cursor-pointer opacity-0', d.dropdown),
        caption_label: cn(
          'text-sm font-semibold tracking-tight',
          captionLayout !== 'label' &&
            'flex h-8 items-center gap-1 rounded-md pl-2 pr-1.5 [&>svg]:size-3.5 [&>svg]:text-muted-foreground',
          d.caption_label,
        ),
        month_grid: cn('w-full border-collapse', d.month_grid),
        weekdays: cn('flex', d.weekdays),
        weekday: cn(
          'w-[--cell-size] select-none pb-1 text-center text-[0.7rem] font-medium uppercase tracking-wider text-muted-foreground/80',
          d.weekday,
        ),
        week: cn('mt-1 flex w-full', d.week),
        day: cn('relative h-[--cell-size] w-[--cell-size] p-0 text-center', d.day),
        today: cn(d.today),
        outside: cn(d.outside),
        disabled: cn(d.disabled),
        hidden: cn('invisible', d.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => (
          <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />
        ),
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={cn('size-4', className)} {...props} />;
          }
          if (orientation === 'right') {
            return <ChevronRightIcon className={cn('size-4', className)} {...props} />;
          }
          return <ChevronDownIcon className={cn('size-4', className)} {...props} />;
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString()}
      data-selected={modifiers.selected || undefined}
      data-today={modifiers.today || undefined}
      data-outside={modifiers.outside || undefined}
      data-disabled={modifiers.disabled || undefined}
      data-weekend={isWeekend || undefined}
      className={cn(
        'relative inline-flex h-[--cell-size] w-[--cell-size] items-center justify-center rounded-lg text-sm tabular-nums',
        'transition-[background-color,color,box-shadow,transform] duration-150 ease-out',
        'hover:bg-accent hover:text-accent-foreground active:scale-95',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        'data-[weekend]:text-muted-foreground',
        // Today: bold with a small dot underneath
        'data-[today]:font-semibold data-[today]:text-foreground',
        "data-[today]:after:absolute data-[today]:after:bottom-1 data-[today]:after:left-1/2 data-[today]:after:h-1 data-[today]:after:w-1 data-[today]:after:-translate-x-1/2 data-[today]:after:rounded-full data-[today]:after:bg-foreground data-[today]:after:content-['']",
        // Outside the shown month
        'data-[outside]:text-muted-foreground/40',
        // Selected
        'data-[selected]:bg-primary data-[selected]:font-semibold data-[selected]:text-primary-foreground data-[selected]:shadow-md data-[selected]:shadow-primary/20',
        'data-[selected]:hover:bg-primary/90 data-[selected]:hover:text-primary-foreground',
        'data-[selected]:after:bg-primary-foreground',
        // Disabled
        'data-[disabled]:pointer-events-none data-[disabled]:text-muted-foreground/30 data-[disabled]:line-through data-[disabled]:decoration-muted-foreground/30',
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
